/**
 * Sound: a cheerful chime for a right answer, a soft one for a wrong one, and
 * gentle background music while a child practises.
 *
 * **Every sound is made here, in code.** There are no audio files. That is a
 * deliberate choice, for three reasons: nothing to license (music is the
 * easiest thing in an app to get wrong legally), nothing to download (the
 * whole engine is a few kilobytes), and nothing that breaks offline.
 *
 * **What the sounds are allowed to mean.** A right answer gets a bright little
 * rising chime — immediate, and about the answer, which is what feedback
 * should be. A wrong answer gets something soft and warm, deliberately NOT a
 * buzzer: in this app a mistake is information, not a telling-off (see the
 * colour notes in themes.ts — red is used sparingly for the same reason).
 *
 * And nothing here rewards volume. There is no rising pitch for a run of right
 * answers, no fanfare for a tenth question. That would be a streak by another
 * name, and engagement-and-reviews.md section 7 sets out why the app does not
 * build those (D-049: nothing is rewarded that can be faked).
 *
 * **Music ducks under speech.** When the app reads a word aloud, the music
 * drops almost to nothing until the voice finishes — music under a spoken
 * word is the one place it would get in the way of learning.
 *
 * **Browsers will not play sound until the person has tapped something.** So
 * the engine wakes itself on the first tap anywhere and stays silent before
 * that, rather than failing loudly.
 */

type Listener = () => void

const STORE_MUSIC = 'ws.sound.music'
const STORE_SFX = 'ws.sound.sfx'
const STORE_MUSIC_VOLUME = 'ws.sound.music.volume'
const STORE_SFX_VOLUME = 'ws.sound.sfx.volume'

/** Remembered per device. Wrapped, because private windows can refuse storage. */
function readOn(key: string): boolean {
  try {
    return window.localStorage.getItem(key) !== 'off'
  } catch {
    return true
  }
}
function writeOn(key: string, on: boolean): void {
  try {
    window.localStorage.setItem(key, on ? 'on' : 'off')
  } catch {
    /* not remembered; still works for this visit */
  }
}
/** A volume from 0 to 1, full if never set. */
function readVolume(key: string): number {
  try {
    const v = Number(window.localStorage.getItem(key))
    return window.localStorage.getItem(key) === null || !Number.isFinite(v) ? 1 : Math.min(1, Math.max(0, v))
  } catch {
    return 1
  }
}
function writeVolume(key: string, v: number): void {
  try {
    window.localStorage.setItem(key, String(v))
  } catch {
    /* not remembered; still works for this visit */
  }
}

// ------------------------------------------------------------------ engine

let ctx: AudioContext | null = null
let master: GainNode | null = null
let musicBus: GainNode | null = null
let sfxBus: GainNode | null = null

// Measured, not guessed: rendered offline through a real Web Audio engine on
// 4 Oct 2026. At 0.16 the music came out 17 dB below the right-answer chime,
// which at an ordinary volume is barely there. At these levels it sits about
// 10 dB under the effects — clearly present, and the chime still stands out.
const MUSIC_LEVEL = 0.38
const DUCKED_LEVEL = 0.05
const SFX_LEVEL = 0.7

let musicOn = typeof window === 'undefined' ? true : readOn(STORE_MUSIC)
let sfxOn = typeof window === 'undefined' ? true : readOn(STORE_SFX)
// Sam, 18 Oct 2026: turning the device down to quieten the music also
// quietens the spoken words, which matter more. So each has its own volume
// here. Speech uses the browser's own voice, outside this engine, and stays
// at the device's volume whatever these are set to.
let musicVolume = typeof window === 'undefined' ? 1 : readVolume(STORE_MUSIC_VOLUME)
let sfxVolume = typeof window === 'undefined' ? 1 : readVolume(STORE_SFX_VOLUME)
const listeners = new Set<Listener>()

function engine(): AudioContext | null {
  if (typeof window === 'undefined') return null
  if (ctx) return ctx
  const Ctor: typeof AudioContext | undefined =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!Ctor) return null
  // On an iPhone or iPad, web audio follows the silent switch but the spoken
  // word does not, so with the switch on a child hears the words and never
  // the chime for a right answer (Charles, 18 Oct 2026). "playback" puts the
  // app's sounds with the speech. Safari 16.4 and later; ignored elsewhere.
  try {
    const session = (navigator as unknown as { audioSession?: { type: string } }).audioSession
    if (session) session.type = 'playback'
  } catch {
    /* an older browser: the switch still decides */
  }
  try {
    ctx = new Ctor()
  } catch {
    return null
  }
  master = ctx.createGain()
  master.gain.value = 1
  // A gentle limiter, so a chime landing on top of the music can never clip.
  const limiter = ctx.createDynamicsCompressor()
  limiter.threshold.value = -10
  limiter.ratio.value = 6
  master.connect(limiter).connect(ctx.destination)

  musicBus = ctx.createGain()
  musicBus.gain.value = 0
  musicBus.connect(master)

  sfxBus = ctx.createGain()
  sfxBus.gain.value = SFX_LEVEL * sfxVolume
  sfxBus.connect(master)
  return ctx
}

/** Call from any tap. Browsers keep audio suspended until the person acts. */
export function unlockAudio(): void {
  const c = engine()
  if (c && c.state === 'suspended') void c.resume().catch(() => undefined)
}

if (typeof window !== 'undefined') {
  const wake = () => unlockAudio()
  window.addEventListener('pointerdown', wake, { passive: true })
  window.addEventListener('keydown', wake)
}

// ------------------------------------------------------------- instruments

/**
 * A soft wooden note, like a marimba: the fundamental plus the bar's own
 * overtone four times higher, which dies away faster. That second partial is
 * what makes it sound struck rather than beeped.
 */
function marimba(bus: AudioNode, freq: number, at: number, dur = 0.5, level = 0.5): void {
  const c = ctx!
  const partials: [number, number, number][] = [[1, 1, dur], [3.98, 0.18, dur * 0.35]]
  for (const [ratio, amp, length] of partials) {
    const osc = c.createOscillator()
    const gain = c.createGain()
    osc.type = 'sine'
    osc.frequency.value = freq * ratio
    gain.gain.setValueAtTime(0, at)
    gain.gain.linearRampToValueAtTime(level * amp, at + 0.006)
    gain.gain.exponentialRampToValueAtTime(0.0001, at + length)
    osc.connect(gain).connect(bus)
    osc.start(at)
    osc.stop(at + length + 0.05)
  }
}

/** A small bright bell, for the sparkle at the top of a right answer. */
function bell(bus: AudioNode, freq: number, at: number, level = 0.25): void {
  const c = ctx!
  for (const [ratio, amp, length] of [[1, 1, 0.9], [2.76, 0.35, 0.5], [5.4, 0.12, 0.3]] as const) {
    const osc = c.createOscillator()
    const gain = c.createGain()
    osc.type = 'sine'
    osc.frequency.value = freq * ratio
    gain.gain.setValueAtTime(0, at)
    gain.gain.linearRampToValueAtTime(level * amp, at + 0.004)
    gain.gain.exponentialRampToValueAtTime(0.0001, at + length)
    osc.connect(gain).connect(bus)
    osc.start(at)
    osc.stop(at + length + 0.05)
  }
}

/** A warm, quiet chord that swells and fades. The bed the music sits on. */
function pad(bus: AudioNode, freqs: number[], at: number, dur: number, level = 0.06): void {
  const c = ctx!
  const filter = c.createBiquadFilter()
  filter.type = 'lowpass'
  filter.frequency.value = 900
  filter.connect(bus)
  for (const f of freqs) {
    const osc = c.createOscillator()
    const gain = c.createGain()
    osc.type = 'triangle'
    osc.frequency.value = f
    gain.gain.setValueAtTime(0, at)
    gain.gain.linearRampToValueAtTime(level, at + 0.4)
    gain.gain.setValueAtTime(level, at + dur - 0.4)
    gain.gain.linearRampToValueAtTime(0, at + dur)
    osc.connect(gain).connect(filter)
    osc.start(at)
    osc.stop(at + dur + 0.05)
  }
}

/** A round, low note for the bass line. */
function bass(bus: AudioNode, freq: number, at: number, dur: number, level = 0.22): void {
  const c = ctx!
  const osc = c.createOscillator()
  const gain = c.createGain()
  osc.type = 'sine'
  osc.frequency.value = freq
  gain.gain.setValueAtTime(0, at)
  gain.gain.linearRampToValueAtTime(level, at + 0.02)
  gain.gain.exponentialRampToValueAtTime(0.0001, at + dur)
  osc.connect(gain).connect(bus)
  osc.start(at)
  osc.stop(at + dur + 0.05)
}

let noise: AudioBuffer | null = null
/** A soft shaker tick: a pinch of filtered noise. Keeps the music moving. */
function shaker(bus: AudioNode, at: number, level = 0.035): void {
  const c = ctx!
  if (!noise) {
    noise = c.createBuffer(1, c.sampleRate * 0.1, c.sampleRate)
    const data = noise.getChannelData(0)
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1
  }
  const src = c.createBufferSource()
  src.buffer = noise
  const filter = c.createBiquadFilter()
  filter.type = 'highpass'
  filter.frequency.value = 6000
  const gain = c.createGain()
  gain.gain.setValueAtTime(level, at)
  gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.06)
  src.connect(filter).connect(gain).connect(bus)
  src.start(at)
  src.stop(at + 0.08)
}

/** Note name to frequency. Equal temperament, A4 = 440. */
const NOTES: Record<string, number> = {}
;(() => {
  const names = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B']
  for (let octave = 1; octave <= 7; octave++) {
    names.forEach((n, i) => {
      NOTES[`${n}${octave}`] = 440 * Math.pow(2, (octave - 4) + (i - 9) / 12)
    })
  }
})()
const hz = (name: string) => NOTES[name] ?? 440

// --------------------------------------------------------------- effects

function canPlayEffect(): AudioContext | null {
  if (!sfxOn) return null
  const c = engine()
  if (!c || !sfxBus) return null
  if (c.state === 'suspended') void c.resume().catch(() => undefined)
  return c
}

export const sfx = {
  /** A right answer: a quick, bright rising arpeggio with a bell on top. */
  correct(): void {
    const c = canPlayEffect()
    if (!c) return
    const t = c.currentTime + 0.01
    marimba(sfxBus!, hz('C5'), t, 0.35, 0.45)
    marimba(sfxBus!, hz('E5'), t + 0.07, 0.35, 0.45)
    marimba(sfxBus!, hz('G5'), t + 0.14, 0.4, 0.5)
    bell(sfxBus!, hz('C6'), t + 0.21, 0.28)
  },

  /**
   * A wrong answer: two soft, warm notes. Quieter than the right-answer chime
   * and deliberately not a buzzer — the child is about to see the right answer,
   * and that is the part worth their attention.
   */
  notYet(): void {
    const c = canPlayEffect()
    if (!c) return
    const t = c.currentTime + 0.01
    marimba(sfxBus!, hz('E4'), t, 0.4, 0.28)
    marimba(sfxBus!, hz('C4'), t + 0.13, 0.55, 0.26)
  },

  /** The end of a session: you are done. Short, warm, and finished. */
  finished(): void {
    const c = canPlayEffect()
    if (!c) return
    const t = c.currentTime + 0.02
    const tune: [string, number][] = [['G4', 0], ['C5', 0.12], ['E5', 0.24], ['G5', 0.36], ['E5', 0.52], ['G5', 0.62]]
    for (const [note, offset] of tune) marimba(sfxBus!, hz(note), t + offset, 0.45, 0.42)
    bell(sfxBus!, hz('C6'), t + 0.78, 0.3)
    pad(sfxBus!, [hz('C4'), hz('E4'), hz('G4')], t + 0.7, 1.4, 0.07)
  },

  /** A word grows in the garden: a little upward "pop", like a shoot appearing. */
  sprout(): void {
    const c = canPlayEffect()
    if (!c) return
    const t = c.currentTime + 0.01
    const osc = c.createOscillator()
    const gain = c.createGain()
    osc.type = 'sine'
    osc.frequency.setValueAtTime(380, t)
    osc.frequency.exponentialRampToValueAtTime(1150, t + 0.12)
    gain.gain.setValueAtTime(0, t)
    gain.gain.linearRampToValueAtTime(0.35, t + 0.01)
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.25)
    osc.connect(gain).connect(sfxBus!)
    osc.start(t)
    osc.stop(t + 0.3)
    bell(sfxBus!, hz('G6'), t + 0.1, 0.12)
  },

  /** A light tap, for opening a trophy or a planet. */
  tap(): void {
    const c = canPlayEffect()
    if (!c) return
    marimba(sfxBus!, hz('A5'), c.currentTime + 0.005, 0.18, 0.22)
  },
}

// ----------------------------------------------------------------- music

/**
 * A happy little loop: four bars of C – A minor – F – G, the friendliest
 * chord sequence there is, at a relaxed walking pace. A marimba plays a tune
 * from the pentatonic scale over a soft pad, a round bass and a shaker.
 *
 * The tune is chosen from hand-written phrases rather than generated note by
 * note, because random notes sound random. Several phrases, picked in turn,
 * so twenty minutes of it does not feel like the same four seconds.
 */
const BPM = 92
const EIGHTH = 60 / BPM / 2
const BAR = EIGHTH * 8

const CHORDS: { pad: string[]; bass: string }[] = [
  { pad: ['C4', 'E4', 'G4'], bass: 'C3' },
  { pad: ['A3', 'C4', 'E4'], bass: 'A2' },
  { pad: ['F3', 'A3', 'C4'], bass: 'F2' },
  { pad: ['G3', 'B3', 'D4'], bass: 'G2' },
]

// Eight eighth-notes a bar. '' is a rest. Each phrase fits its chord.
const PHRASES: string[][][] = [
  [
    ['E5', '', 'G5', 'E5', 'D5', '', 'C5', ''],
    ['C5', '', 'E5', '', 'A4', 'C5', '', ''],
    ['A4', '', 'C5', 'A4', 'G4', '', 'A4', ''],
    ['G4', '', 'B4', 'D5', '', 'G5', '', ''],
  ],
  [
    ['G5', 'E5', '', 'C5', 'D5', '', 'E5', ''],
    ['E5', '', 'C5', '', 'A4', '', 'E5', 'D5'],
    ['C5', '', 'A4', 'C5', 'F5', '', 'A5', ''],
    ['G5', '', 'D5', '', 'B4', 'D5', 'G5', ''],
  ],
  [
    ['C5', 'D5', 'E5', '', 'G5', '', 'E5', ''],
    ['A5', '', 'E5', '', 'C5', '', 'E5', ''],
    ['F5', '', 'C5', '', 'A4', '', 'C5', 'D5'],
    ['D5', '', 'G4', 'B4', 'D5', '', '', ''],
  ],
]

let playing = false
let timer: number | null = null
let nextBarAt = 0
let barIndex = 0
let phraseIndex = 0
let ducked = false

function scheduleBar(at: number): void {
  const bus = musicBus!
  // The indexes are always in range (modulo the length), so these never fall
  // back; the fallbacks are only there to satisfy the type checker.
  const chord = CHORDS[barIndex % CHORDS.length] ?? CHORDS[0]!
  const phrase = PHRASES[phraseIndex % PHRASES.length]?.[barIndex % CHORDS.length] ?? []

  pad(bus, chord.pad.map(hz), at, BAR + 0.05, 0.05)
  bass(bus, hz(chord.bass), at, EIGHTH * 3)
  bass(bus, hz(chord.bass), at + EIGHTH * 4, EIGHTH * 3, 0.16)
  for (let i = 0; i < 8; i++) {
    if (i % 2 === 1) shaker(bus, at + i * EIGHTH)
    const note = phrase[i]
    if (note) marimba(bus, hz(note), at + i * EIGHTH, 0.42, 0.34)
  }

  barIndex++
  if (barIndex % CHORDS.length === 0) phraseIndex++
}

/** Look a little ahead and book the next bar in, so timing never wobbles. */
function tick(): void {
  if (!ctx || !playing) return
  while (nextBarAt < ctx.currentTime + 0.2) {
    scheduleBar(nextBarAt)
    nextBarAt += BAR
  }
}

/** Where the music should sit now: its own volume, and lower under speech. */
function musicTarget(): number {
  return (ducked ? DUCKED_LEVEL : MUSIC_LEVEL) * musicVolume
}

function setMusicLevel(level: number, over = 0.6): void {
  if (!ctx || !musicBus) return
  const now = ctx.currentTime
  musicBus.gain.cancelScheduledValues(now)
  musicBus.gain.setValueAtTime(musicBus.gain.value, now)
  musicBus.gain.linearRampToValueAtTime(level, now + over)
}

export const music = {
  /** Start the loop, fading in. Safe to call when it is already playing. */
  start(): void {
    if (!musicOn) return
    const c = engine()
    if (!c || !musicBus) return
    if (c.state === 'suspended') void c.resume().catch(() => undefined)
    if (playing) return
    playing = true
    barIndex = 0
    nextBarAt = c.currentTime + 0.1
    setMusicLevel(musicTarget(), 1.5)
    tick()
    timer = window.setInterval(tick, 50)
  },

  /** Fade out and stop. */
  stop(): void {
    if (!playing) return
    playing = false
    setMusicLevel(0, 0.6)
    if (timer !== null) {
      window.clearInterval(timer)
      timer = null
    }
  },

  /** Drop the music almost to nothing while a word is being spoken. */
  duck(on: boolean): void {
    ducked = on
    if (playing) setMusicLevel(musicTarget(), on ? 0.15 : 0.8)
  },

  isPlaying(): boolean {
    return playing
  },
}

// -------------------------------------------------------------- settings

export function soundSettings(): { music: boolean; sfx: boolean; musicVolume: number; sfxVolume: number } {
  return { music: musicOn, sfx: sfxOn, musicVolume, sfxVolume }
}

/** 0 to 1. Changes the music as it plays. */
export function setMusicVolume(v: number): void {
  musicVolume = Math.min(1, Math.max(0, v))
  writeVolume(STORE_MUSIC_VOLUME, musicVolume)
  if (playing) setMusicLevel(musicTarget(), 0.2)
  listeners.forEach((l) => l())
}

/** 0 to 1, for the chimes and taps. */
export function setSfxVolume(v: number): void {
  sfxVolume = Math.min(1, Math.max(0, v))
  writeVolume(STORE_SFX_VOLUME, sfxVolume)
  if (ctx && sfxBus) sfxBus.gain.setValueAtTime(SFX_LEVEL * sfxVolume, ctx.currentTime)
  listeners.forEach((l) => l())
}

export function setMusicOn(on: boolean): void {
  musicOn = on
  writeOn(STORE_MUSIC, on)
  if (!on) music.stop()
  listeners.forEach((l) => l())
}

export function setSfxOn(on: boolean): void {
  sfxOn = on
  writeOn(STORE_SFX, on)
  if (on) sfx.tap()
  listeners.forEach((l) => l())
}

/** For the toggle buttons, so they redraw when the setting changes. */
export function onSoundChange(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}
